(function () {
    'use strict';

    angular
        .module('editorApp')
        .factory('QiniuToken', QiniuToken);

    QiniuToken.$inject = ['Qinius', '$q', 'UidService', '$rootScope'];

    /**
     * 七牛上传 token 公共管理服务：
     * 全局缓存 token，按 action+params 分 key，
     * 到期前自动刷新，订阅者（subscribe）在刷新后自动拿到新 token。
     * 使用原生 setTimeout（token 不做模板绑定，无需 digest，与原各处循环行为一致）。
     */
    function QiniuToken(Qinius, $q, UidService, $rootScope) {
        // key -> { token, expireAt, timerId, inFlight, action, params, subscribers: [] }
        var records = {};

        var MIN_WAIT_MS = 1000;          // expireAt 异常时的下限，避免热循环
        var DEFAULT_TTL_MS = 3600000;    // expireAt 缺失时兜底 1 小时
        var FETCH_ERROR_RETRY_MS = 30000;// 后端获取失败时 30s 后重试，保持单例存活

        var service = {
            subscribe: subscribe,
            get: get,
            getPromise: getPromise,
            getSync: getSync,
            refresh: refresh,
            uploadThumbnail: uploadThumbnail
        };

        return service;

        // ---------------- 公共 API ----------------

        /**
         * fn(token字符串) 在缓存新鲜时立即回调（同步），否则等获取完成后回调；
         * 之后每次自动刷新都会再次回调。返回取消订阅函数。
         */
        function subscribe(action, params, fn) {
            var key = makeKey(action, params);
            var rec = ensureRecord(key);
            rec.subscribers.push(fn);
            if (isFresh(rec)) {
                notify(fn, rec.token);
            } else {
                ensureFetch(action, params, key, rec);
            }
            ensureTimer(key, rec);
            return function unsubscribe() {
                var idx = rec.subscribers.indexOf(fn);
                if (idx !== -1) {
                    rec.subscribers.splice(idx, 1);
                }
                // 没有订阅者时暂停定时器，缓存保留供 get()/getSync() 使用
                if (rec.subscribers.length === 0 && !rec.inFlight) {
                    clearTimeout(rec.timerId);
                    rec.timerId = null;
                }
            };
        }

        /**
         * 一次性获取：fn(token字符串)，缓存新鲜时同步回调，否则获取完成后回调。不保留订阅。
         */
        function get(action, params, fn) {
            var key = makeKey(action, params);
            var rec = ensureRecord(key);
            if (isFresh(rec)) {
                notify(fn, rec.token);
                ensureTimer(key, rec);
                return;
            }
            ensureFetch(action, params, key, rec).then(function (token) {
                notify(fn, token);
            });
        }

        /**
         * 兼容原 $resource promise 用法：返回 { token, $promise }，
         * token 在 $promise resolve 前就填充好，
         * 可直接替换 vm.tokens = Qinius.get(...) + $q.all + vm.tokens.token。
         */
        function getPromise(action, params) {
            var key = makeKey(action, params);
            var rec = ensureRecord(key);
            var obj = { token: isFresh(rec) ? rec.token : null, $promise: null };
            if (isFresh(rec)) {
                obj.$promise = $q.when(rec.token);
                ensureTimer(key, rec);
            } else {
                obj.$promise = ensureFetch(action, params, key, rec).then(function (token) {
                    obj.token = token;
                    return token;
                });
            }
            return obj;
        }

        /**
         * 同步读取缓存 token（不发请求）；缓存不存在或已过期返回 undefined。
         */
        function getSync(action, params) {
            var rec = records[makeKey(action, params)];
            return (rec && isFresh(rec)) ? rec.token : undefined;
        }

        /**
         * 强制重新获取（绕过缓存），成功后通知订阅者并回调 fn(token字符串)。
         */
        function refresh(action, params, fn) {
            var key = makeKey(action, params);
            var rec = ensureRecord(key);
            if (rec.inFlight) {
                rec.inFlight.then(function (token) {
                    notify(fn, token);
                });
                return;
            }
            clearTimeout(rec.timerId);
            rec.timerId = null;
            ensureFetch(action, params, key, rec, true).then(function (token) {
                notify(fn, token);
            });
        }

        /**
         * 上传缩略图到七牛（key '3dThumbnail-' + UidService.get()）。
         * input 可为 canvas 或 dataURL 字符串；token 失效(401/"expired token")时
         * 自动 refresh 对应 action 的 token 并重试一次；
         * errorback 仅在非 token 错误或重试仍失败时触发。
         */
        function uploadThumbnail(input, callback, errorback, action, params) {
            var url = typeof input === 'string' ? input : input.toDataURL("image/png");
            doUploadThumbnail(dataURLtoBlob(url), '3dThumbnail-' + UidService.get(), callback, errorback, action || 'get', params || {}, false);
        }

        function doUploadThumbnail(blob, key, callback, errorback, action, params, isRetry) {
            var formData = new FormData();
            formData.append('file', blob);
            formData.append('key', key);
            formData.append('token', getSync(action, params) || '');
            $.ajax({
                cache: true,
                type: "POST",
                url: 'https://upload.qiniup.com/',
                data: formData,
                async: false,
                contentType: false,
                processData: false,
                success: function (data) {
                    if (callback) { callback(data); }
                    safeApply();
                },
                error: function (request) {
                    if (!isRetry && request) {
                        refresh(action, params, function () {
                            doUploadThumbnail(blob, key, callback, errorback, action, params, true);
                        });
                    } else {
                        if (errorback) { errorback(request); }
                        safeApply();
                    }
                }
            });
        }

        function safeApply() {
            // 首次尝试运行在 html2canvas 回调（无 digest）→ 触发 apply；
            // 重试路径运行在 $resource 回调（digest 进行中）→ 守卫跳过，避免 $digest already in progress
            if (!$rootScope.$$phase) {
                $rootScope.$apply();
            }
        }

        // ---------------- 内部实现 ----------------

        function makeKey(action, params) {
            return action + '|' + JSON.stringify(params || {});
        }

        function ensureRecord(key) {
            if (!records[key]) {
                records[key] = { token: null, expireAt: 0, timerId: null, inFlight: null, action: null, params: null, subscribers: [] };
            }
            return records[key];
        }

        function isFresh(rec) {
            return !!rec.token && rec.expireAt > new Date().getTime();
        }

        function ensureFetch(action, params, key, rec, force) {
            if (!force && (rec.inFlight || isFresh(rec))) {
                return rec.inFlight || $q.when(rec.token);
            }
            rec.action = action;
            rec.params = params;
            var promise = $q(function (resolve, reject) {
                var ok = function (msg) {
                    if (rec.inFlight !== promise) {
                        return;
                    }
                    rec.token = msg.token;
                    rec.expireAt = msg.expireAt || (new Date().getTime() + DEFAULT_TTL_MS);
                    rec.inFlight = null;
                    ensureTimer(key, rec);
                    rec.subscribers.forEach(function (fn) {
                        notify(fn, rec.token);
                    });
                    resolve(rec.token);
                };
                var fail = function () {
                    if (rec.inFlight !== promise) {
                        return;
                    }
                    rec.inFlight = null;
                    ensureTimer(key, rec, FETCH_ERROR_RETRY_MS);
                    reject();
                };
                Qinius[action](params || {}, ok, fail);
            });
            rec.inFlight = promise;
            return promise;
        }

        function notify(fn, token) {
            if (typeof fn === 'function') {
                fn(token);
            }
        }

        function ensureTimer(key, rec, delay) {
            clearTimeout(rec.timerId);
            rec.timerId = null;
            if (delay) {
                rec.timerId = setTimeout(function () {
                    rec.timerId = null;
                    ensureFetch(rec.action, rec.params, key, rec);
                }, delay);
            } else if (rec.subscribers.length > 0 && rec.expireAt) {
                var wait = Math.max(MIN_WAIT_MS, rec.expireAt - new Date().getTime());
                rec.timerId = setTimeout(function () {
                    rec.timerId = null;
                    ensureFetch(rec.action, rec.params, key, rec);
                }, wait);
            }
        }
    }
})();
